import { Box, Divider, Tab, Tabs } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import { Typography } from 'src/components/new/Typography';
import { Button } from 'src/components/new/Button';
import {
  getMeteredRates,
  getPostageRatesv2,
  updateMeteredInputs,
  updatePostageRatesV2,
} from 'src/features/admin/utils';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import PendingIcon from '@mui/icons-material/Pending';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import BoltIcon from '@mui/icons-material/Bolt';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import { clearUnsavedMeteredInputs, clearUnsavedPostageRates } from 'src/features/admin/adminSlice';
import CertifiedPostageRates from './components/CertifiedPostageRates';
import MeteredInputs from './components/MeteredInputs';
import BkLoader from 'src/components/loader/BkLoader';
import FirstClassPostageRates from './components/FirstClassPostageRates';
import PriorityPostageRates from './components/PriorityPostageRates';
import { useSearchParams } from 'react-router-dom';

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const PostageRates: React.FC = () => {
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditingEnabled, setIsEditingEnabled] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const unsavedPostageRates = useSelector((state: RootState) => state.admin.unsavedPostageRates);
  const unsavedMeteredInputs = useSelector((state: RootState) => state.admin.unsavedMeteredInputs);

  const dispatch = useDispatch<AppDispatch>();

  const availableTabs = [
    {
      label: 'First Class',
      icon: <MailOutlinedIcon fontSize="small" />,
      component: <FirstClassPostageRates isEditingEnabled={isEditingEnabled} />,
      queryParam: 'firstclass',
    },
    {
      label: 'Certified Mail',
      icon: <GppGoodOutlinedIcon fontSize="small" />,
      component: <CertifiedPostageRates />,
      queryParam: 'certified',
    },
    {
      label: 'Priority Express',
      icon: <BoltIcon />,
      component: <PriorityPostageRates isEditingEnabled={isEditingEnabled} />,
      queryParam: 'priorityexpress',
    },
  ];

  const onEditClick = () => {
    setIsEditingEnabled(true);
  };

  useEffect(() => {
    dispatch(getPostageRatesv2());
    dispatch(getMeteredRates());
  }, []);

  const activeTab = searchParams.get('subtab');
  const activeTabIndex = Math.max(
    0,
    availableTabs.findIndex((tab) => tab.queryParam === activeTab),
  );

  useEffect(() => {
    const isValid = availableTabs.some((tab) => tab.queryParam === activeTab);

    if (!activeTab || !isValid) {
      setSearchParams(
        (prev) => {
          prev.set('subtab', availableTabs[0].queryParam);
          return prev;
        },
        { replace: true },
      );
    }
  }, [activeTab, availableTabs, setSearchParams]);

  const onDiscardChangesClick = () => {
    setIsEditingEnabled(false);
    dispatch(clearUnsavedPostageRates());
    dispatch(clearUnsavedMeteredInputs());
    setShowSnackbarMsg({ msg: 'Changes discarded!', type: 'success' });
  };

  const onSaveChangesClick = () => {
    const formattedPostageRates = Object.entries(unsavedPostageRates).map((item) => ({
      id: +item[0],
      price: +item[1].slice(1),
    }));
    const shouldUpdateMeteredInputs = Object.keys(unsavedMeteredInputs).length !== 0;
    const shouldUpdatePostageRates = Object.keys(unsavedPostageRates).length !== 0;

    if (
      Object.values(unsavedMeteredInputs).some((item) => item.length === 1) ||
      Object.values(unsavedPostageRates).some((item) => item.length === 1)
    ) {
      setShowSnackbarMsg({ msg: 'Please complete all fields before saving', type: 'error' });
      return;
    }
    if (shouldUpdateMeteredInputs) {
      dispatch(updateMeteredInputs(unsavedMeteredInputs)).then((response) => {
        if (updateMeteredInputs.fulfilled.match(response)) {
          setShowSnackbarMsg({ msg: 'Changes saved successfully!', type: 'success' });
          if (!shouldUpdatePostageRates) {
            dispatch(getPostageRatesv2());
          }
          dispatch(getMeteredRates()).then(() => {
            dispatch(clearUnsavedMeteredInputs());
          });
        }
      });
    }
    if (shouldUpdatePostageRates) {
      setShowLoader(true);
      dispatch(updatePostageRatesV2(formattedPostageRates)).then((response) => {
        if (updatePostageRatesV2.rejected.match(response)) {
          setShowSnackbarMsg({ msg: 'Something went wrong!', type: 'error' });
          setShowLoader(false);
        }
        if (updatePostageRatesV2.fulfilled.match(response)) {
          dispatch(getPostageRatesv2()).then(() => {
            setShowSnackbarMsg({ msg: 'Changes saved successfully!', type: 'success' });
            setShowLoader(false);
            dispatch(clearUnsavedPostageRates());
          });
        }
      });
    }
    setIsEditingEnabled(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSearchParams((prev) => {
      prev.set('subtab', availableTabs[newValue].queryParam);
      return prev;
    });
    setIsEditingEnabled(false);
    dispatch(clearUnsavedPostageRates());
  };

  useEffect(() => {
    return () => {
      dispatch(clearUnsavedPostageRates());
    };
  }, []);

  const totalUnsavedChanges = [...Object.keys(unsavedMeteredInputs), ...Object.keys(unsavedPostageRates)].length;

  return (
    <>
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
      <BkLoader loaderTitle="Updating postage rates..." open={showLoader} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 3 }}>
          <Typography sx={{ fontFamily: 'ABC Arizona Mix' }} fontWeight={300} fontSize={32}>
            Postage Rates
          </Typography>
          {totalUnsavedChanges !== 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: '#ffeedf',
                borderRadius: '999px',
                padding: 1,
              }}
            >
              <PendingIcon sx={{ display: 'inline', color: '#7b3f07' }} />
              <Typography sx={{ color: '#7b3f07', fontWeight: 500 }}>{totalUnsavedChanges} unsaved changes</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flex: 1.5 }}>
          {isEditingEnabled && (
            <>
              <Button startIcon={<DeleteIcon fontSize="small" />} type="text" onClick={onDiscardChangesClick}>
                Discard Changes
              </Button>
              <Button startIcon={<CheckIcon fontSize="small" />} type="primary" onClick={onSaveChangesClick}>
                Save Changes
              </Button>
            </>
          )}
          {!isEditingEnabled && (
            <Button
              disabled={activeTabIndex === 1}
              startIcon={<EditIcon fontSize="small" />}
              type="primary"
              onClick={onEditClick}
            >
              Edit Postage Sheet
            </Button>
          )}
          <Button startIcon={<FileDownloadOutlinedIcon />} type="outline">
            Export
          </Button>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Tabs
          TabIndicatorProps={{
            sx: { backgroundColor: '#436eb2' },
          }}
          value={activeTabIndex}
          onChange={handleTabChange}
        >
          {availableTabs.map((tab) => (
            <Tab
              key={`tab_${tab.label}`}
              label={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {tab.icon}
                  <Typography fontWeight={500}>{tab.label}</Typography>
                </Box>
              }
              sx={{
                textTransform: 'none',
                fontFamily: 'Instrument Sans',
                fontSize: '1rem',
                lineHeight: '1.25rem',
                textAlign: 'center',
                color: '#45464F',
                '&.Mui-selected': {
                  color: '#436eb2',
                },
              }}
            />
          ))}
        </Tabs>
        <Divider />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {availableTabs[activeTabIndex]?.component || <Typography>No content available</Typography>}
        <MeteredInputs isEditingEnabled={isEditingEnabled} />
      </Box>
    </>
  );
};

export default PostageRates;
